import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Sparkles, Loader2, ChevronDown, Languages } from 'lucide-react';
import { getLanguages } from '../redux/translationSlice';
import { translateAppointmentSummary, getAppointmentSummary } from '../redux/appointmentSlice';
import { getSocket } from '../socket';
import { isSummaryTranslationLanguage, baseLanguageCode } from '../data/summaryTranslationLanguages';
import { serverReason } from './patientTabs/prescriptionHelpers';

const EMPTY_LANGUAGES = [];

// Clinical AI summary for the appointment — generated on completion in the
// doctor's language. A language dropdown (from master → Translation → Language)
// lets the viewer switch language; the summary is AI-translated (and cached)
// on demand, with a "Generating…" state. Shown only for completed appointments.
//
// The dropdown offers only the languages the server translates into
// (data/summaryTranslationLanguages.js, mirrored from the backend): the master
// table can hold more, and the server answers any other code with
// 400 UNSUPPORTED_LANGUAGE. A refusal is shown as a sentence, never a blank.
//
// A row flagged `clinicalHidden` (a caller the clinical-record rule refuses)
// renders nothing and asks for nothing: the server would refuse the summary,
// and the parent screen says why the clinical details are not shown.
//
// `generationOn` (owner, 2 Oct 2026): the tenant's `ai_summary` AND the
// doctor's plan `ai_appointment_summary` (useVisitAiGates). Off, no summary is
// coming: the server is asked ONCE — a summary stored before is still shown —
// and nothing is drawn while it answers or when it has none (no spinner, no
// poll). The server says the same on its own (`featureDisabled` /
// `notInPlan`), for a visit whose doctor's plan this screen cannot see.
export default function AppointmentSummary({ appointment, className = '', generationOn = true }) {
    const { t, i18n } = useTranslation(['appointmentdetails']);
    const dispatch = useDispatch();
    const clinicalHidden = appointment?.clinicalHidden === true;
    const isCompleted = /completed/i.test(appointment?.status || '') && !clinicalHidden;
    const languages = useSelector((s) => s.translation?.languages || EMPTY_LANGUAGES);

    // While the background summary is being generated we poll for it. `fetched`
    // holds the summary once it lands; `pendingGen` means inputs (transcript /
    // STT / OCR) are still generating.
    const [fetched, setFetched] = useState(null);
    const [pendingGen, setPendingGen] = useState(false);

    // Stored as JSON: { lang, text, translations: { <code>: text } }. Falls back
    // to plain text for any legacy value.
    const parsed = useMemo(() => {
        const raw = appointment?.aiAppointmentSummary || fetched;
        if (!raw) return null;
        try {
            const o = JSON.parse(raw);
            if (o && typeof o === 'object' && (o.text || o.translations)) {
                return { lang: o.lang || 'en', text: o.text || '', translations: o.translations || {} };
            }
        } catch (e) { /* legacy plain text */ }
        return { lang: 'en', text: String(raw), translations: {} };
    }, [appointment?.aiAppointmentSummary, fetched]);

    const [selectedLang, setSelectedLang] = useState(parsed?.lang || 'en');
    const [cache, setCache] = useState(parsed?.translations || {});
    const [loading, setLoading] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [translateError, setTranslateError] = useState('');
    const menuRef = useRef(null);

    // Reset to the source language when the appointment changes.
    useEffect(() => {
        setSelectedLang(parsed?.lang || 'en');
        setCache(parsed?.translations || {});
        setMenuOpen(false);
        setTranslateError('');
        setNoneComing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appointment?.appointmentId]);

    useEffect(() => {
        if (isCompleted && languages.length === 0) {
            dispatch(getLanguages({ page: 1, limit: 100, search: '' }));
        }
    }, [isCompleted, languages.length, dispatch]);

    // Live push: the backend emits summary:ready the moment the summary is
    // stored → flip the loader to the summary instantly (no poll wait). The
    // socket may not be connected yet at mount, so retry attaching.
    useEffect(() => {
        const id = appointment?.appointmentId;
        if (!id) return undefined;
        let alive = true;
        let retryTimer = null;
        let attachedSocket = null;
        const onReady = (p) => { if (p && p.appointmentId === id && p.summary) setFetched(p.summary); };
        const attach = () => {
            if (!alive) return;
            const socket = getSocket && getSocket();
            if (!socket) { retryTimer = setTimeout(attach, 1500); return; }
            attachedSocket = socket;
            socket.on('summary:ready', onReady);
        };
        attach();
        return () => {
            alive = false;
            if (retryTimer) clearTimeout(retryTimer);
            if (attachedSocket) attachedSocket.off('summary:ready', onReady);
        };
    }, [appointment?.appointmentId]);

    // Poll for the background-generated summary until it lands — BOUNDED so a
    // permanently-failed generation can't hammer the API forever (~10 min max;
    // the backend self-heals + throttles retries on its side).
    const [stalled, setStalled] = useState(false);
    // 1 Oct 2026 (owner): the summary is the `ai_appointment_summary` plan
    // control of the VISIT's doctor. Without it the server makes none and
    // answers `notInPlan: true` — stop polling and draw nothing, instead of a
    // loader that spins for ten minutes. A summary written before is still
    // returned and still shown. 2 Oct 2026: the tenant's `ai_summary` switch
    // off answers `featureDisabled: true` — the same.
    const [noneComing, setNoneComing] = useState(false);
    useEffect(() => {
        const id = appointment?.appointmentId;
        if (!isCompleted || !id) return undefined;
        if (appointment?.aiAppointmentSummary || fetched) return undefined; // already have it
        let alive = true;
        let timer = null;
        let attempts = 0;
        const MAX_ATTEMPTS = 100;
        const tick = () => {
            attempts += 1;
            dispatch(getAppointmentSummary(id)).unwrap()
                .then((res) => {
                    if (!alive || !res) return;
                    setPendingGen(!!res.pending);
                    if (res.summary) { setFetched(res.summary); return; }
                    // Nothing will be made: asked once, never polled.
                    if (res.notInPlan || res.featureDisabled || !generationOn) { setNoneComing(true); return; }
                    if (attempts >= MAX_ATTEMPTS) { setStalled(true); return; }
                    timer = setTimeout(tick, 6000);
                })
                .catch(() => {
                    if (!alive) return;
                    if (!generationOn) { setNoneComing(true); return; }
                    if (attempts >= MAX_ATTEMPTS) { setStalled(true); return; }
                    timer = setTimeout(tick, 8000);
                });
        };
        tick();
        return () => { alive = false; if (timer) clearTimeout(timer); };
    }, [isCompleted, appointment?.appointmentId, appointment?.aiAppointmentSummary, fetched, dispatch, generationOn]);

    useEffect(() => {
        const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
        if (menuOpen) { document.addEventListener('mousedown', onClick); return () => document.removeEventListener('mousedown', onClick); }
    }, [menuOpen]);

    if (!isCompleted) return null;

    const hasSummary = !!parsed?.text;
    // No summary coming (the server said so, or this screen's gate is off —
    // then nothing is drawn while the one question is answered either).
    if ((noneComing || !generationOn) && !hasSummary) return null;
    const currentText = cache[selectedLang] || (selectedLang === parsed?.lang ? parsed?.text : '');
    // The source language is always offered (switching back to it asks the
    // server nothing); every other entry must be one the server translates into.
    const offeredLanguages = languages.filter((l) => l?.code && (
        isSummaryTranslationLanguage(l.code) || l.code === parsed?.lang
    ));
    const langName = (code) => languages.find((l) => l.code === code)?.name || code;

    const pickLang = async (code, name) => {
        setMenuOpen(false);
        if (!code || code === selectedLang) return;
        setTranslateError('');
        if (cache[code] || code === parsed?.lang) { setSelectedLang(code); return; } // cached or source — no re-translate
        if (!isSummaryTranslationLanguage(code)) {
            setTranslateError(t('appointmentdetails.summary_language_unsupported', 'The summary cannot be translated into this language.'));
            return;
        }
        const previous = selectedLang;
        setSelectedLang(code);
        setLoading(true);
        try {
            const res = await dispatch(translateAppointmentSummary({
                appointmentId: appointment.appointmentId, lang: code, languageName: name,
            })).unwrap();
            if (res?.summary) {
                setCache((c) => ({ ...c, [code]: res.summary }));
            } else {
                setSelectedLang(previous);
                setTranslateError(t('appointmentdetails.summary_translate_failed', 'The summary could not be translated. Please try again.'));
            }
        } catch (e) {
            // Back to the language that has text, with the reason — an empty
            // box under "Tamil" reads as a summary that says nothing.
            setSelectedLang(previous);
            const unsupported = e?.status === 400 || e?.data?.code === 'UNSUPPORTED_LANGUAGE';
            setTranslateError(unsupported
                ? serverReason(e, t('appointmentdetails.summary_language_unsupported', 'The summary cannot be translated into this language.'), baseLanguageCode(i18n.language) || 'en')
                : serverReason(e, t('appointmentdetails.summary_translate_failed', 'The summary could not be translated. Please try again.'), baseLanguageCode(i18n.language) || 'en'));
        } finally { setLoading(false); }
    };

    return (
        <div data-testid="appointment-summary" className={`rounded-2xl border border-[#3AA8A8]/25 bg-gradient-to-br from-[#3AA8A8]/8 to-transparent p-4 sm:p-5 ${className}`}>
            <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#2D8A8A]" />
                    <h4 className="text-sm sm:text-base font-bold text-[#2D8A8A]">
                        {t('appointmentdetails.appointment_summary', 'Appointment summary')}
                    </h4>
                </div>

                {hasSummary && offeredLanguages.length > 0 && (
                    <div className="relative" ref={menuRef}>
                        <button
                            type="button"
                            data-testid="appointment-summary-lang-button"
                            onClick={() => setMenuOpen((v) => !v)}
                            className="flex items-center gap-1 text-xs font-semibold text-[#3AA8A8] hover:text-[#2D8A8A] border border-[#4ABDBD]/30 rounded-lg px-2 py-1"
                        >
                            <Languages className="w-3.5 h-3.5" />
                            {langName(selectedLang)}
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {menuOpen && (
                            <div className="absolute right-0 top-full mt-1 z-30 w-40 max-h-56 overflow-y-auto custom-scrollbar bg-white rounded-xl shadow-lg border border-gray-200 py-1" data-testid="appointment-summary-lang-menu">
                                {offeredLanguages.map((l) => (
                                    <button
                                        key={l.code || l.languageId}
                                        type="button"
                                        data-testid={`appointment-summary-lang-option-${l.code}`}
                                        onClick={() => pickLang(l.code, l.name)}
                                        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 ${l.code === selectedLang ? 'text-[#2D8A8A] font-semibold' : 'text-gray-700'}`}
                                    >
                                        {l.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {translateError && (
                <p role="alert" data-testid="appointment-summary-translate-error" className="mb-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs sm:text-sm text-amber-700">
                    {translateError}
                </p>
            )}

            {!hasSummary ? (
                stalled ? (
                    <p data-testid="appointment-summary-stalled" className="text-sm text-gray-400 py-1">
                        {t('appointmentdetails.summary_delayed', 'The summary is taking longer than expected — it will appear here once ready.')}
                    </p>
                ) : (
                    <div data-testid="appointment-summary-generating" className="py-1">
                        <div className="flex items-center gap-2 text-sm text-amber-600">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {pendingGen
                                ? t('appointmentdetails.summary_waiting', 'Waiting for transcripts to finish…')
                                : t('appointmentdetails.generating_summary_full', 'Generating summary…')}
                        </div>
                        {/* Indeterminate progress bar (a true % isn't available from the LLM). */}
                        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#3AA8A8]/10">
                            <div className="h-full w-1/3 rounded-full bg-[#3AA8A8]/60 animate-pulse" />
                        </div>
                    </div>
                )
            ) : loading ? (
                <div data-testid="appointment-summary-generating" className="flex items-center gap-2 text-sm text-amber-600 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('appointmentdetails.generating_summary', 'Generating…')}
                </div>
            ) : (
                <p data-testid="appointment-summary-text" className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                    {currentText}
                </p>
            )}
        </div>
    );
}
